import { usePathname } from 'next/navigation';
import { useCallback } from 'react';

import { useBreadcrumbTrail } from '@/data/Content/BreadcrumbTrail';
import { usePersonInfo } from '@/data/Content/PersonInfo';
import { useSettings } from '@/data/Settings';
import { getExternalId } from '@/data/utils/externalId';
import { PinterestClientPayload } from '@/pages/api/tracking/pinterest/pinterestAPIUtils';
import { usePinterestClickIdentifiers } from 'presentation/events/hooks/useClickIdentifiers';
import { useTrackingStoreLocation } from 'presentation/events/hooks/useTrackingStoreLocation';
import { CATEGORY_URL_PATH } from 'presentation/events/hooks/useTrackSearchSubmit/constants';
import { triggerPinterestCAPIEvent } from 'presentation/events/services/pinterestService';

import { safeEncodeUrl } from 'presentation/events/utils/ga4Utils';
import { SendMediaEventProps } from './types';

export const useTrackViewCategoryPinterest = () => {
	const { settings } = useSettings();
	const { userData } = settings;
	const { personInfo } = usePersonInfo();
	const { breadcrumb } = useBreadcrumbTrail();
	const { epik } = usePinterestClickIdentifiers();
	const storeLocation = useTrackingStoreLocation();
	const pathname = usePathname();

	const shouldSendViewCategory =
		userData?.ENABLE_PINTEREST_CALL === 'true' && !!pathname?.startsWith(CATEGORY_URL_PATH);

	const sendPinterestEvent = useCallback(
		({ storeId, eventId }: SendMediaEventProps) => {
			if (!shouldSendViewCategory) return;

			const content_category = breadcrumb
				.filter((cat) => cat.type === undefined)
				.map((cat) => cat.label)
				.join(' > ');

			const payload: PinterestClientPayload = {
				event_name: 'view_category',
				event_id: eventId,
				storeId,
				event_source_url: safeEncodeUrl(window.location.href),
				epik,
				test: userData?.ENABLE_PINTEREST_TEST === 'true',
				custom_data: {
					content_category,
					externalId: getExternalId(),
					email: personInfo?.email1,
					phone: personInfo?.phone1,
					firstName: personInfo?.firstName,
					lastName: personInfo?.lastName,
					city: storeLocation?.city,
					state: storeLocation?.state,
					zip: storeLocation?.zipCode,
				},
			};

			triggerPinterestCAPIEvent(payload);
		},
		[
			breadcrumb,
			epik,
			shouldSendViewCategory,
			storeLocation,
			personInfo?.email1,
			personInfo?.firstName,
			personInfo?.lastName,
			personInfo?.phone1,
			userData?.ENABLE_PINTEREST_TEST,
		]
	);

	return { sendPinterestEvent, shouldSendViewCategory };
};
